// src/pages/admin/AdminUsers.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Papa from 'papaparse';
import {
  collection,
  getDocs,
  doc,
  query,
  getDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { USER_ROLES, APPLICATION_STATUS, updateUserPlatformFee, DEFAULT_PLATFORM_FEE_PERCENTAGE } from '../../services/UserRolesService';
import { deleteUserAndData } from '../../services/DeleteUserService';
import './Admin.css';

function AdminUsers() {
  const { currentUser, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [applicationsData, setApplicationsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [editingFeeUserId, setEditingFeeUserId] = useState(null);
  const [tempFeeValue, setTempFeeValue] = useState('');

  // Fetch all users and attempt repair for broken ones
  useEffect(() => {
    const fetchAndRepairUsers = async () => {
      setError(null);
      console.log("AdminUsers: Starting user fetch...");

      try {
        const usersQuery = query(
          collection(db, 'users')
        );
        const usersSnapshot = await getDocs(usersQuery);

        console.log(`AdminUsers: Raw querySnapshot size: ${usersSnapshot.size}`);
        if (usersSnapshot.size < 10 || usersSnapshot.empty) {
            console.log("AdminUsers: Logging details of fetched raw documents:");
            usersSnapshot.docs.forEach(d => console.log(` - Raw Fetched Doc ID: ${d.id}`, d.data()));
        }

        let usersList = usersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log(`AdminUsers: Mapped ${usersList.length} initial user documents from snapshot.`);

        if (usersList.length === 0 && !usersSnapshot.empty) {
             console.warn("AdminUsers: Mapping resulted in an empty list, but snapshot was not empty.");
        } else if (usersList.length === 0) {
             console.log("AdminUsers: No users found in the 'users' collection mapping.");
             setUsers([]);
             setLoading(false);
             return;
        }

        const appFetchPromises = usersList.map(user =>
          getDoc(doc(db, 'serviceProviderApplications', user.id))
            .then(appDoc => ({ userId: user.id, appDoc, status: 'fulfilled' }))
            .catch(err => ({ userId: user.id, error: err, status: 'rejected' }))
        );
        const appResults = await Promise.allSettled(appFetchPromises);

        const appDataMap = new Map();
        appResults.forEach(result => {
          if (result.status === 'fulfilled' && result.value.status === 'fulfilled') {
            const { userId, appDoc } = result.value;
            if (appDoc && appDoc.exists()) { appDataMap.set(userId, appDoc.data()); }
          } else if (result.status === 'fulfilled' && result.value.status === 'rejected') {
              console.warn(`AdminUsers: Failed to fetch app doc for ${result.value.userId}:`, result.value.error);
          } else if (result.status === 'rejected') {
              console.error(`AdminUsers: Promise.allSettled reported rejection for an app fetch:`, result.reason);
          }
        });

        const applicationsObject = {};
        appDataMap.forEach((data, userId) => {
          applicationsObject[userId] = data;
        });
        setApplicationsData(applicationsObject);

        const repairedUsersList = usersList.map(user => {
           const repairedUser = { ...user };
           const needsRepair = !repairedUser.displayName || !repairedUser.email;

          if (needsRepair) {
            const appData = appDataMap.get(user.id);
            if (appData) {
              if (!repairedUser.displayName && appData.displayName) { repairedUser.displayName = appData.displayName; }
              if (!repairedUser.email && appData.email) { repairedUser.email = appData.email; }
            }
          }
          // Ensure fallbacks AFTER potential repair
          repairedUser.displayName = repairedUser.displayName || '(Name Missing)';
          repairedUser.email = repairedUser.email || '(Email Missing)';
          repairedUser.role = repairedUser.role || USER_ROLES.USER;
          repairedUser.status = repairedUser.status || null;
          repairedUser.platformFeePercentage = repairedUser.platformFeePercentage || DEFAULT_PLATFORM_FEE_PERCENTAGE;
          return repairedUser;
        });

        console.log(`AdminUsers: Setting final users state with ${repairedUsersList.length} users.`);
        setUsers(repairedUsersList);

      } catch (err) {
        console.error("AdminUsers: Error in fetchAndRepairUsers:", err);
        if (err.code === 'permission-denied') {
             setError("Permission denied. Check Firestore Security Rules.");
        } else {
            setError(`Failed to load users: ${err.message}`);
        }
        setUsers([]);
      } finally {
        setLoading(false);
        console.log("AdminUsers: Finished user fetch/repair process.");
      }
    };

    if (!authLoading) {
        console.log("AdminUsers: AuthContext loaded, proceeding with fetch.");
        fetchAndRepairUsers();
    } else {
        console.log("AdminUsers: Waiting for AuthContext to finish loading...");
    }

  }, [authLoading]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    if (timestamp && typeof timestamp.toDate === 'function') {
        try { return timestamp.toDate().toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }); }
        catch (e) { console.error("Error formatting Firestore Timestamp:", e, "Timestamp:", timestamp); return 'Invalid Date'; }
    }
    else if (typeof timestamp === 'string') {
        try {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) { return date.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }); }
            else { return 'Invalid Date String'; }
        } catch (e) { console.error("Error formatting date string:", e, "Timestamp:", timestamp); return 'Invalid Date'; }
    }
    else if (timestamp instanceof Date) {
         try { return timestamp.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }); }
         catch (e) { console.error("Error formatting Date object:", e, "Timestamp:", timestamp); return 'Invalid Date'; }
    }
    console.warn("Unrecognized date format:", timestamp); return 'Unknown Format';
  };
  
  const getRoleName = (role) => {
    switch(role) {
      case USER_ROLES.ADMIN: return 'Administrator';
      case USER_ROLES.SERVICE_PROVIDER: return 'Service Provider';
      case USER_ROLES.USER: return 'Regular User';
      default: return 'User';
    }
  };
  
  const getStatusName = (status) => {
      switch(status) {
          case APPLICATION_STATUS.APPROVED: return 'Approved';
          case APPLICATION_STATUS.PENDING: return 'Pending Review';
          case APPLICATION_STATUS.REJECTED: return 'Rejected';
          default: return 'N/A';
      }
  };
  
  const getStatusClass = (status) => {
        switch(status) {
            case APPLICATION_STATUS.APPROVED: return 'approved';
            case APPLICATION_STATUS.PENDING: return 'pending';
            case APPLICATION_STATUS.REJECTED: return 'rejected';
            default: return 'inactive';
        }
    };
    
   const getRoleClass = (role) => {
        switch(role) {
            case USER_ROLES.ADMIN: return 'admin';
            case USER_ROLES.SERVICE_PROVIDER: return 'service_provider';
            case USER_ROLES.USER: return 'user';
            default: return 'user';
        }
    };

  const handleDeleteUser = async (userId, userName) => {
    if (userId === currentUser?.uid) { setError("You cannot delete your own admin account from this interface."); setTimeout(() => setError(null), 3000); return; }
    if (!window.confirm(`Are you sure you want to permanently delete the user "${userName || 'ID: '+userId}" and all their associated listings? This action cannot be undone.`)) { return; }
    setError(null); setSuccess(null);
    try {
      await deleteUserAndData(userId);
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      setSuccess(`User "${userName || 'ID: '+userId}" and their data were successfully deleted.`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) { console.error("Error deleting user:", err); setError(`Failed to delete user "${userName || 'ID: '+userId}". Error: ${err.message}`); }
  };

  // Start editing platform fee
  const startEditingFee = (userId, currentFee) => {
    setEditingFeeUserId(userId);
    setTempFeeValue(currentFee.toString());
  };

  // Cancel editing
  const cancelEditingFee = () => {
    setEditingFeeUserId(null);
    setTempFeeValue('');
  };

  // Save platform fee
  const savePlatformFee = async (userId) => {
    const feeValue = parseFloat(tempFeeValue);
    
    if (isNaN(feeValue) || feeValue < 1 || feeValue > 20) {
      setError("Platform fee must be between 1% and 20%");
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      await updateUserPlatformFee(userId, feeValue);
      
      // Update local state
      setUsers(prevUsers => prevUsers.map(user => 
        user.id === userId ? { ...user, platformFeePercentage: feeValue } : user
      ));
      
      setSuccess("Platform fee updated successfully");
      setTimeout(() => setSuccess(null), 3000);
      
      setEditingFeeUserId(null);
      setTempFeeValue('');
    } catch (err) {
      console.error("Error updating platform fee:", err);
      setError(`Failed to update platform fee: ${err.message}`);
      setTimeout(() => setError(null), 3000);
    }
  };

  // Function to export users as CSV
  const exportUsersAsCSV = () => {
    setIsExporting(true);
    
    try {
      const csvData = users.map(user => {
        const appData = applicationsData[user.id] || {};
        
        const formattedCreatedAt = user.createdAt ? formatDate(user.createdAt) : 'N/A';
        const formattedUpdatedAt = user.updatedAt ? formatDate(user.updatedAt) : 'N/A';
        const formattedSubmittedAt = appData.submittedAt ? formatDate(appData.submittedAt) : 'N/A';
        const formattedReviewedAt = appData.reviewedAt ? formatDate(appData.reviewedAt) : 'N/A';
        
        return {
          id: user.id,
          displayName: user.displayName || '(Name Missing)',
          email: user.email || '(Email Missing)',
          role: getRoleName(user.role),
          roleCode: user.role,
          status: getStatusName(user.status),
          statusCode: user.status,
          platformFeePercentage: user.platformFeePercentage || DEFAULT_PLATFORM_FEE_PERCENTAGE,
          stripeConnectAccountId: user.stripeConnectAccountId || 'N/A',
          stripeOnboardingStatus: user.stripeOnboardingStatus || 'N/A',
          createdAt: formattedCreatedAt,
          updatedAt: formattedUpdatedAt,
          photoURL: user.photoURL || 'N/A',
          isCurrentAdmin: user.id === currentUser?.uid ? 'Yes' : 'No',
          
          hasApplication: appData ? 'Yes' : 'No',
          applicationStatus: appData.status || 'N/A',
          businessName: appData.businessName || 'N/A',
          businessType: appData.businessType || 'N/A',
          phoneNumber: appData.phoneNumber || 'N/A',
          address: appData.address || 'N/A',
          applicationSubmittedAt: formattedSubmittedAt,
          applicationReviewedAt: formattedReviewedAt,
          website: appData.website || 'N/A',
          description: appData.description || 'N/A',
          rejectionReason: appData.rejectionReason || 'N/A',
          additionalInfo: appData.additionalInfo || 'N/A'
        };
      });

      const csv = Papa.unparse(csvData, {
        quotes: true,
        header: true,
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `refounded-users-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess("CSV export completed successfully!");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      setError(`Failed to export users data: ${err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const combinedLoading = authLoading || loading;

  if (combinedLoading) {
    return <div className="admin-container">Loading users data...</div>;
  }

  if (error && users.length === 0) {
     return <div className="admin-container error-message">{error}</div>;
  }

  return (
    <div className="admin-container">
       <div className="admin-header"><h1>User Management</h1><p>View and manage user accounts</p></div>
      {success && <div className="success-message">{success}</div>}
      {error && <div className="error-message">{error}</div>}
      <div className="admin-menu"><Link to="/admin" className="admin-menu-item">Applications</Link><Link to="/admin/users" className="admin-menu-item active">Users</Link><Link to="/admin/listings" className="admin-menu-item">Listings</Link></div>

      <div className="admin-content">
        <div className="section-header">
          <h2>All Users</h2>
          <div className="section-actions">
            <button 
              className="export-csv-button" 
              onClick={exportUsersAsCSV} 
              disabled={isExporting || users.length === 0}
            >
              {isExporting ? 'Exporting...' : 'Export as CSV'}
            </button>
            <p>{users.length} user account(s) displayed</p>
          </div>
        </div>

        {users.length === 0 && !combinedLoading ? (
          <div className="empty-state">
            <p>No users found or processed.</p>
            {error && <p style={{color: 'red', marginTop: '10px'}}>Error during loading: {error}</p>}
          </div>
        ) : (
          <div className="users-table-container">
            <table className="admin-table">
               <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Provider Status</th>
                  <th>Platform Fee</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const isProvider = user.role === USER_ROLES.SERVICE_PROVIDER || 
                                   (user.role === USER_ROLES.ADMIN && user.status === APPLICATION_STATUS.APPROVED);
                  const canEditFee = isProvider;
                  
                  return (
                    <tr key={user.id}>
                      <td>
                        <div className="user-info">
                          <span className="user-name">{user.displayName}</span>
                          <span className="user-email">{user.email}</span>
                          {user.id === currentUser?.uid && ( <span className="user-current">(You)</span> )}
                        </div>
                      </td>
                      <td><span className={`role-badge ${getRoleClass(user.role)}`}>{getRoleName(user.role)}</span></td>
                      <td><span className={`status-badge ${getStatusClass(user.status)}`}>{(user.role === USER_ROLES.SERVICE_PROVIDER || user.status) ? getStatusName(user.status) : 'N/A'}</span></td>
                      <td>
                        {canEditFee ? (
                          editingFeeUserId === user.id ? (
                            <div className="platform-fee-edit">
                              <input
                                type="number"
                                min="1"
                                max="20"
                                value={tempFeeValue}
                                onChange={(e) => setTempFeeValue(e.target.value)}
                                className="fee-input"
                              />
                              <span>%</span>
                              <button 
                                onClick={() => savePlatformFee(user.id)}
                                className="fee-save-btn"
                                title="Save"
                              >
                                ✓
                              </button>
                              <button 
                                onClick={cancelEditingFee}
                                className="fee-cancel-btn"
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="platform-fee-display">
                              <span>{user.platformFeePercentage || DEFAULT_PLATFORM_FEE_PERCENTAGE}%</span>
                              <button 
                                onClick={() => startEditingFee(user.id, user.platformFeePercentage || DEFAULT_PLATFORM_FEE_PERCENTAGE)}
                                className="fee-edit-btn"
                                title="Edit platform fee"
                              >
                                ✎
                              </button>
                            </div>
                          )
                        ) : (
                          <span className="fee-na">N/A</span>
                        )}
                      </td>
                      <td>{formatDate(user.createdAt)}</td>
                      <td>
                        {(currentUser && user.id !== currentUser.uid) ? (
                          <button onClick={() => handleDeleteUser(user.id, user.displayName)} className="delete-action" title={`Delete user ${user.displayName || user.id} and all their data`}>Delete User</button>
                        ) : ( <span style={{fontSize: '0.8rem', color: '#999'}}>Cannot delete self</span> )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminUsers;